import { formatMinor, type BillingOverview } from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, Input, Pill } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  billingKeys,
  billingOverviewQuery,
  openBillingPortal,
  setAiCeiling,
  startCheckout,
} from '../../shared/api/billing-queries.js';

/**
 * Who pays, and what reading costs.
 *
 * The account half belongs to whoever opened it: trial, plan, the payment
 * page. The spend half is per business: what AI reading has cost this month
 * against the ceiling an owner set, so a bad batch is a visible number and
 * never a surprise invoice.
 */

type Account = NonNullable<BillingOverview['account']>;

export function daysLeft(iso: string | null, now = new Date()): number | null {
  if (iso === null) return null;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - now.getTime()) / 86_400_000));
}

export function standingSentence(account: Account, now = new Date()): string {
  const covered = `${String(account.businessesCovered)} business${account.businessesCovered === 1 ? '' : 'es'}`;
  switch (account.status) {
    case 'complimentary':
      return `Complimentary. Covers ${covered}.`;
    case 'active':
      return `Paid${account.currentPeriodEnd === null ? '' : ` through ${account.currentPeriodEnd.slice(0, 10)}`}. Covers ${covered}.`;
    case 'past_due':
      return `The last payment did not go through. Everything still works while it is retried; update the card to be safe. Covers ${covered}.`;
    case 'canceled':
      return `Cancelled. The books stay readable and exportable; reading new documents is paused.`;
    case 'trialing': {
      const left = daysLeft(account.trialEndsAt, now);
      if (left === 0)
        return 'The trial has ended. Reading new documents is paused until billing is set up; nothing is lost.';
      return `Trial, ${String(left)} day${left === 1 ? '' : 's'} left. Covers ${covered}.${account.hasPaymentMethod ? ' Billing starts when it ends.' : ''}`;
    }
  }
}

export function BillingCard() {
  const overview = useQuery(billingOverviewQuery());
  const [error, setError] = useState<string | null>(null);
  const go = (url: string) => window.location.assign(url);

  const checkout = useMutation({
    mutationFn: startCheckout,
    onSuccess: (result) => go(result.url),
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'Billing could not open.'),
  });
  const portal = useMutation({
    mutationFn: openBillingPortal,
    onSuccess: (result) => go(result.url),
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'Billing could not open.'),
  });

  const data = overview.data;
  if (data === undefined) return null;
  const account = data.account;

  return (
    <Card>
      <CardHeader
        title="Billing and AI spend"
        description={
          account === null
            ? 'These businesses are paid for by whoever opened them.'
            : `${account.name}, ${account.kind === 'firm' ? 'billed per client business' : 'one business'}.`
        }
        actions={
          account === null ||
          account.status === 'complimentary' ? null : account.hasPaymentMethod ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => portal.mutate()}
              isLoading={portal.isPending}
            >
              Cards and invoices
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => checkout.mutate()}
              isLoading={checkout.isPending}
              disabled={!data.paymentsEnabled}
            >
              Set up billing
            </Button>
          )
        }
      />
      <CardBody className="flex flex-col gap-4 text-sm">
        {error === null ? null : <Alert tone="warning">{error}</Alert>}
        {account === null ? null : (
          <p className="flex flex-wrap items-center gap-2">
            <Pill tone={account.inGoodStanding ? 'success' : 'warning'}>
              {account.status === 'trialing' ? 'Trial' : account.status.replace('_', ' ')}
            </Pill>
            <span className="text-content">{standingSentence(account)}</span>
          </p>
        )}
        {account !== null &&
        !account.hasPaymentMethod &&
        !data.paymentsEnabled &&
        account.status === 'trialing' ? (
          <p className="text-xs text-content-muted">
            Payments are not switched on yet, so the trial simply carries on.
          </p>
        ) : null}
        <SpendTable businesses={data.businesses} />
      </CardBody>
    </Card>
  );
}

function SpendTable({ businesses }: { businesses: BillingOverview['businesses'] }) {
  if (businesses.length === 0) return null;
  return (
    <div>
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-content-subtle">
        AI reading this month
      </p>
      <ul className="divide-y divide-border rounded-[var(--radius-control)] border border-border">
        {businesses.map((business) => (
          <SpendRow key={business.organizationId} business={business} />
        ))}
      </ul>
      <p className="mt-2 text-xs text-content-muted">
        At the ceiling, new documents wait instead of being read; nothing is lost, and they are read
        when the ceiling is raised or the month turns. Amounts are close estimates of the provider's
        charges.
      </p>
    </div>
  );
}

function SpendRow({ business }: { business: BillingOverview['businesses'][number] }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [dollars, setDollars] = useState(String(Number(business.ceilingMinor) / 100));
  const save = useMutation({
    mutationFn: () =>
      setAiCeiling(business.organizationId, String(Math.round(Number(dollars) * 100))),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: billingKeys.overview });
    },
  });
  const spent = BigInt(business.spentMinor);
  const ceiling = BigInt(business.ceilingMinor);
  const share = ceiling === 0n ? 100 : Math.min(100, Number((spent * 100n) / ceiling));
  const valid = /^\d+(\.\d{1,2})?$/.test(dollars) && Number(dollars) <= 100_000;

  return (
    <li className="flex flex-wrap items-center gap-3 px-3 py-2">
      <span className="min-w-[10rem] flex-1 text-content">{business.name}</span>
      <span className="tabular text-content-muted">
        {formatMinor(business.spentMinor)} of {formatMinor(business.ceilingMinor)}
      </span>
      <span className="h-1.5 w-24 overflow-hidden rounded-full bg-surface-sunken" aria-hidden>
        <span
          className={`block h-full ${share >= 100 ? 'bg-danger' : share >= 80 ? 'bg-warning' : 'bg-brand-500'}`}
          style={{ width: `${String(share)}%` }}
        />
      </span>
      {!business.canChangeCeiling ? null : editing ? (
        <span className="flex items-center gap-2">
          <span className="text-content-muted">$</span>
          <Input
            className="h-8 w-24"
            value={dollars}
            aria-label={`Monthly AI ceiling for ${business.name}`}
            onChange={(event) => setDollars(event.target.value)}
          />
          <Button
            size="sm"
            onClick={() => save.mutate()}
            isLoading={save.isPending}
            disabled={!valid}
          >
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </span>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
          Change ceiling
        </Button>
      )}
    </li>
  );
}
